import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  useTheme,
  alpha,
  Stack,
  Paper,
  Chip,
  Avatar,
  AvatarGroup,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VerifiedIcon from '@mui/icons-material/Verified';
import DiamondIcon from '@mui/icons-material/Diamond';
import PublicIcon from '@mui/icons-material/Public';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { coins } from "../data/coins";

// Styled Components
const AuctionCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 16,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const LiveBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  left: 16,
  zIndex: 2,
  backgroundColor: '#f44336',
  color: '#FFFFFF',
  padding: '4px 12px',
  borderRadius: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.9, transform: 'scale(1.02)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
}));

const DiamondBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 2,
  backgroundColor: alpha(theme.palette.primary.main, 0.95),
  borderRadius: '20px',
  padding: '4px 10px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const PriceTag = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderRadius: 8,
  padding: '8px 12px',
  marginTop: 8,
  marginBottom: 8,
}));

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 4 }}>{children}</Box>}
  </div>
);

const Auctions = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [watchlist, setWatchlist] = useState([]);
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [bidStep, setBidStep] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [auctionCoins, setAuctionCoins] = useState([]);
  
  // Bid form state
  const [bidForm, setBidForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    bidAmount: '',
    maxBid: '',
    bidType: 'regular',
    agreeTerms: false
  });

  useEffect(() => {
    // Filter coins that are eligible for auction (premium coins)
    const eligibleCoins = coins.filter(coin => 
      coin.gradeNum >= 65 || 
      coin.hasDiamond === true || 
      coin.metal === 'Gold'
    );
    
    // Transform coins into auction format with auction-specific data
    const formattedAuctions = eligibleCoins.map((coin, index) => ({
      ...coin,
      auctionId: index + 1,
      currentBid: coin.price,
      currentBidNum: coin.priceNum,
      minNextBid: coin.price,
      bids: Math.floor(Math.random() * 30) + 5,
      timeLeft: `${Math.floor(Math.random() * 5) + 1}d ${Math.floor(Math.random() * 23)}h`,
      viewers: Math.floor(Math.random() * 300) + 50,
      auctioneer: ["Heritage Auctions", "Stack's Bowers", "Diamond Auctions", "Sotheby's"][Math.floor(Math.random() * 4)],
      startTime: `${Math.floor(Math.random() * 7) + 1}d ${Math.floor(Math.random() * 23)}h`,
      interested: Math.floor(Math.random() * 150) + 20,
      date: `Mar ${Math.floor(Math.random() * 20) + 1}, 2024`,
    }));
    
    setAuctionCoins(formattedAuctions);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleWatchlist = (auctionId) => {
    if (watchlist.includes(auctionId)) {
      setWatchlist(watchlist.filter(id => id !== auctionId));
    } else {
      setWatchlist([...watchlist, auctionId]);
    }
  };

  const handleOpenBidModal = (auction) => {
    setSelectedAuction(auction);
    setBidStep(1);
    setBidForm({
      fullName: '',
      email: '',
      phone: '',
      bidAmount: '',
      maxBid: '',
      bidType: 'regular',
      agreeTerms: false
    });
    setBidModalOpen(true);
  };

  const handleCloseBidModal = () => {
    setBidModalOpen(false);
    setSelectedAuction(null);
  };

  const handleBidFormChange = (e) => {
    setBidForm({
      ...bidForm,
      [e.target.name]: e.target.value
    });
  };

  const handleBidNext = () => {
    setBidStep(2);
  };

  const handleBidBack = () => {
    setBidStep(1);
  };

  const handleSubmitBid = () => {
    const subject = `Bid Submitted: ${selectedAuction?.name}`;
    const body = `
      BID SUBMISSION
      
      Auction Details:
      - Coin: ${selectedAuction?.name}
      - Year: ${selectedAuction?.year}
      - Grade: ${selectedAuction?.grade}
      - Current Bid: ${selectedAuction?.currentBid}
      
      Bidder Information:
      - Full Name: ${bidForm.fullName}
      - Email: ${bidForm.email}
      - Phone: ${bidForm.phone}
      - Bid Amount: $${bidForm.bidAmount}
      - Max Bid: $${bidForm.maxBid || 'N/A'}
      - Bid Type: ${bidForm.bidType === 'regular' ? 'Regular Bid' : 'Maximum Bid'}
    `;

    const mailtoLink = `mailto:auctions@diamondcoins.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    
    setSnackbar({
      open: true,
      message: 'Your bid has been submitted successfully! You will receive a confirmation email.',
      severity: 'success'
    });
    
    handleCloseBidModal();
  };

  // Split auctions into different categories
  const liveAuctions = auctionCoins.slice(0, 6);
  const upcomingAuctions = auctionCoins.slice(6, 9);
  const pastAuctions = auctionCoins.slice(9, 12);
  const featuredAuction = auctionCoins[0];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      backgroundColor: '#F5F5F5', 
      minHeight: '100vh' 
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                fontWeight: 400,
                mb: 2
              }}
            >
              Elite Numismatic Auctions
            </Typography>
            
            <Typography 
              variant="h2" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                color: '#2D2D2D',
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}
            >
              Live & Online
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                Rare Coin Auctions
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto',
                color: '#616161',
                fontSize: '1.1rem',
                lineHeight: 1.8
              }}
            >
              Participate in our exclusive auctions featuring the world's rarest and most valuable coins.
              Register to bid on exceptional pieces from renowned collections.
            </Typography>
          </Box>
        </motion.div>

        {/* Featured Auction - Same size as other cards */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ color: '#2D2D2D', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalFireDepartmentIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            Featured Auction
          </Typography>
          
          <Grid container spacing={3}>
            {featuredAuction && (
              <Grid item xs={12} sm={6} lg={4}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <AuctionCard>
                    <LiveBadge>
                      <Box sx={{ width: 6, height: 6, backgroundColor: '#FFFFFF', borderRadius: '50%' }} />
                      LIVE
                    </LiveBadge>
                    
                    {featuredAuction.hasDiamond && (
                      <DiamondBadge>
                        <DiamondIcon sx={{ color: '#FFFFFF', fontSize: 12 }} />
                        <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.6rem' }}>
                          DIAMOND
                        </Typography>
                      </DiamondBadge>
                    )}

                    <CardMedia
                      component="img"
                      image={featuredAuction.image}
                      alt={featuredAuction.name}
                      sx={{
                        height: 220,
                        objectFit: 'contain',
                        backgroundColor: '#FAFAFA',
                        p: 2,
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200?text=Coin';
                      }}
                    />

                    <CardContent sx={{ p: 2.5, pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D2D2D', fontSize: '0.95rem', lineHeight: 1.3 }}>
                          {featuredAuction.name}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleWatchlist(featuredAuction.id)}
                          sx={{ 
                            color: watchlist.includes(featuredAuction.id) ? 'primary.main' : '#9E9E9E',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {watchlist.includes(featuredAuction.id) ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                        </IconButton>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip label={featuredAuction.year} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#F5F5F5' }} />
                        <Chip label={featuredAuction.grade} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#F5F5F5' }} />
                      </Stack>

                      <PriceTag>
                        <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block' }}>
                          Current Bid
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem' }}>
                          {featuredAuction.currentBid}
                        </Typography>
                      </PriceTag>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ color: '#f44336', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 500 }}>
                            {featuredAuction.timeLeft}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GavelIcon sx={{ color: '#9E9E9E', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {featuredAuction.bids} bids
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VisibilityIcon sx={{ color: '#9E9E9E', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {featuredAuction.viewers}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2.5, pt: 0 }}>
                      <Button 
                        variant="contained" 
                        fullWidth
                        onClick={() => handleOpenBidModal(featuredAuction)}
                        endIcon={<GavelIcon />}
                        sx={{ 
                          py: 1,
                          borderRadius: 2,
                          backgroundColor: 'primary.main',
                          color: '#000000',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        Place Bid
                      </Button>
                    </CardActions>
                  </AuctionCard>
                </motion.div>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 2, borderColor: alpha('#C6A75E', 0.2), mb: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: '#616161',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                minWidth: 120,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3,
              },
            }}
          >
            <Tab label="Live Auctions" />
            <Tab label="Upcoming" />
            <Tab label="Past Results" />
            <Tab label="My Watchlist" />
          </Tabs>
        </Box>

        {/* Live Auctions Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {liveAuctions.map((auction) => (
              <Grid item xs={12} sm={6} lg={4} key={auction.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <AuctionCard>
                    <LiveBadge>
                      <Box sx={{ width: 6, height: 6, backgroundColor: '#FFFFFF', borderRadius: '50%' }} />
                      LIVE
                    </LiveBadge>
                    
                    {auction.hasDiamond && (
                      <DiamondBadge>
                        <DiamondIcon sx={{ color: '#FFFFFF', fontSize: 12 }} />
                        <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.6rem' }}>
                          DIAMOND
                        </Typography>
                      </DiamondBadge>
                    )}

                    <CardMedia
                      component="img"
                      image={auction.image}
                      alt={auction.name}
                      sx={{
                        height: 220,
                        objectFit: 'contain',
                        backgroundColor: '#FAFAFA',
                        p: 2,
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200?text=Coin';
                      }}
                    />

                    <CardContent sx={{ p: 2.5, pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D2D2D', fontSize: '0.95rem', lineHeight: 1.3 }}>
                          {auction.name}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleWatchlist(auction.id)}
                          sx={{ 
                            color: watchlist.includes(auction.id) ? 'primary.main' : '#9E9E9E',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {watchlist.includes(auction.id) ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                        </IconButton>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip label={auction.year} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#F5F5F5' }} />
                        <Chip label={auction.grade} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#F5F5F5' }} />
                      </Stack>

                      <PriceTag>
                        <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block' }}>
                          Current Bid
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1rem' }}>
                          {auction.currentBid}
                        </Typography>
                      </PriceTag>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ color: '#f44336', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 500 }}>
                            {auction.timeLeft}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GavelIcon sx={{ color: '#9E9E9E', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {auction.bids} bids
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <VisibilityIcon sx={{ color: '#9E9E9E', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                            {auction.viewers}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2.5, pt: 0 }}>
                      <Button 
                        variant="contained" 
                        fullWidth
                        onClick={() => handleOpenBidModal(auction)}
                        endIcon={<GavelIcon />}
                        sx={{ 
                          py: 1,
                          borderRadius: 2,
                          backgroundColor: 'primary.main',
                          color: '#000000',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        Place Bid
                      </Button>
                    </CardActions>
                  </AuctionCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Upcoming Auctions Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {upcomingAuctions.map((auction) => (
              <Grid item xs={12} md={4} key={auction.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: '#FFFFFF',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha('#C6A75E', 0.1)}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${alpha('#C6A75E', 0.12)}`,
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        component="img"
                        src={auction.image}
                        alt={auction.name}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'contain',
                          backgroundColor: '#FAFAFA',
                          borderRadius: 2,
                          p: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 0.5, fontSize: '0.9rem' }}>
                          {auction.name}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip label={auction.year} size="small" sx={{ height: 22, fontSize: '0.6rem' }} />
                          <Chip label={auction.grade} size="small" sx={{ height: 22, fontSize: '0.6rem' }} />
                        </Stack>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block', mb: 0.5 }}>
                        Estimated Price
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.95rem' }}>
                        {auction.currentBid}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WatchLaterIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.75rem' }}>
                          Starts in {auction.startTime}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${auction.interested} interested`}
                        size="small"
                        sx={{ backgroundColor: alpha('#C6A75E', 0.1), fontSize: '0.6rem', height: 22 }}
                      />
                    </Box>

                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<NotificationsActiveIcon />}
                      sx={{ 
                        borderRadius: 2,
                        borderColor: alpha('#C6A75E', 0.3),
                        color: '#616161',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: alpha('#C6A75E', 0.04),
                        },
                      }}
                    >
                      Notify Me
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Past Results Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {pastAuctions.map((auction) => (
              <Grid item xs={12} md={4} key={auction.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: '#FFFFFF',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha('#C6A75E', 0.1)}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${alpha('#C6A75E', 0.1)}`,
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        component="img"
                        src={auction.image}
                        alt={auction.name}
                        sx={{
                          width: 70,
                          height: 70,
                          objectFit: 'contain',
                          backgroundColor: '#FAFAFA',
                          borderRadius: 2,
                          p: 1,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 0.5, fontSize: '0.85rem' }}>
                          {auction.name}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip label={auction.year} size="small" sx={{ height: 22, fontSize: '0.6rem' }} />
                          <Chip label={auction.grade} size="small" sx={{ height: 22, fontSize: '0.6rem' }} />
                        </Stack>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block' }}>
                          Final Price
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.9rem' }}>
                          {auction.currentBid}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block' }}>
                          Bids
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600, fontSize: '0.85rem' }}>
                          {auction.bids}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block' }}>
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600, fontSize: '0.85rem' }}>
                          {auction.date}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      variant="text"
                      fullWidth
                      endIcon={<HistoryIcon />}
                      sx={{ mt: 2, color: '#616161', '&:hover': { color: 'primary.main' }, fontSize: '0.75rem' }}
                    >
                      View Details
                    </Button>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Watchlist Tab */}
        <TabPanel value={tabValue} index={3}>
          {watchlist.length > 0 ? (
            <Grid container spacing={3}>
              {liveAuctions
                .filter(auction => watchlist.includes(auction.id))
                .map((auction) => (
                  <Grid item xs={12} sm={6} lg={4} key={auction.id}>
                    <AuctionCard>
                      <LiveBadge sx={{ top: 12, left: 12 }}>
                        <Box sx={{ width: 6, height: 6, backgroundColor: '#FFFFFF', borderRadius: '50%' }} />
                        LIVE
                      </LiveBadge>
                      
                      <CardMedia
                        component="img"
                        image={auction.image}
                        alt={auction.name}
                        sx={{
                          height: 200,
                          objectFit: 'contain',
                          backgroundColor: '#FAFAFA',
                          p: 2,
                        }}
                      />

                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2D2D2D', fontSize: '0.9rem' }}>
                          {auction.name}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.95rem' }}>
                            {auction.currentBid}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 500 }}>
                            {auction.timeLeft}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ p: 2.5, pt: 0 }}>
                        <Button 
                          variant="contained" 
                          fullWidth
                          onClick={() => handleOpenBidModal(auction)}
                          endIcon={<GavelIcon />}
                          sx={{ borderRadius: 2 }}
                        >
                          Place Bid
                        </Button>
                      </CardActions>
                    </AuctionCard>
                  </Grid>
                ))}
            </Grid>
          ) : (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                borderRadius: 3,
                border: `1px solid ${alpha('#C6A75E', 0.1)}`,
              }}
            >
              <FavoriteBorderIcon sx={{ color: '#9E9E9E', fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
                Your Watchlist is Empty
              </Typography>
              <Typography variant="body2" sx={{ color: '#616161', mb: 3 }}>
                Add items to your watchlist to track auctions you're interested in.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setTabValue(0)}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#000000',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                Browse Live Auctions
              </Button>
            </Paper>
          )}
        </TabPanel>

        {/* Price Information Box */}
        <Box 
          sx={{ 
            mt: 6, 
            p: 3, 
            backgroundColor: alpha('#C6A75E', 0.05),
            border: `1px solid ${alpha('#C6A75E', 0.2)}`,
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="#616161">
            <strong>Price Ranges:</strong> Prices shown are estimated market ranges based on current market conditions. 
            Final pricing depends on grade, certification, and market demand. Click "Place Bid" for specific pricing and availability.
          </Typography>
        </Box>
      </Container>

      {/* Bid Modal - Keep as is */}
      <Dialog 
        open={bidModalOpen} 
        onClose={handleCloseBidModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', position: 'relative' }}>
          <IconButton
            onClick={handleCloseBidModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#9E9E9E'
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <GavelIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          </Box>
          
          <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
            Place Your Bid
          </Typography>
          
          {selectedAuction && (
            <Typography variant="body2" color="#616161" sx={{ mb: 2 }}>
              {selectedAuction.name} | Current Bid: {selectedAuction.currentBid}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          {bidStep === 1 ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#2D2D2D', fontWeight: 600, mb: 2 }}>
                Bidder Information
              </Typography>
              
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={bidForm.fullName}
                onChange={handleBidFormChange}
                margin="dense"
                required
                size="small"
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                }}
              />
              
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={bidForm.email}
                onChange={handleBidFormChange}
                margin="dense"
                required
                size="small"
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                }}
              />
              
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={bidForm.phone}
                onChange={handleBidFormChange}
                margin="dense"
                required
                size="small"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                }}
              />

              <Typography variant="subtitle2" sx={{ color: '#2D2D2D', fontWeight: 600, mt: 3, mb: 2 }}>
                Bid Details
              </Typography>

              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Bid Type</InputLabel>
                <Select
                  name="bidType"
                  value={bidForm.bidType}
                  onChange={handleBidFormChange}
                  label="Bid Type"
                >
                  <MenuItem value="regular">Regular Bid</MenuItem>
                  <MenuItem value="maximum">Maximum Bid (Auto-bid)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Your Bid Amount ($)"
                name="bidAmount"
                type="number"
                value={bidForm.bidAmount}
                onChange={handleBidFormChange}
                margin="dense"
                required
                size="small"
                InputProps={{
                  startAdornment: <AttachMoneyIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                }}
                helperText={selectedAuction ? `Minimum next bid: ${selectedAuction.minNextBid}` : ''}
              />

              {bidForm.bidType === 'maximum' && (
                <TextField
                  fullWidth
                  label="Maximum Bid Amount ($)"
                  name="maxBid"
                  type="number"
                  value={bidForm.maxBid}
                  onChange={handleBidFormChange}
                  margin="dense"
                  size="small"
                  InputProps={{
                    startAdornment: <AttachMoneyIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                  }}
                  helperText="Auto-bid up to this amount"
                />
              )}
            </Box>
          ) : (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <VerifiedIcon sx={{ color: 'primary.main', fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
                Confirm Your Bid
              </Typography>
              
              <Paper sx={{ p: 3, backgroundColor: alpha('#C6A75E', 0.05), mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>Bidder</Typography>
                    <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                      {bidForm.fullName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>Email</Typography>
                    <Typography variant="body2" sx={{ color: '#2D2D2D' }}>
                      {bidForm.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>Bid Amount</Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      ${bidForm.bidAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>Bid Type</Typography>
                    <Typography variant="body2" sx={{ color: '#2D2D2D' }}>
                      {bidForm.bidType === 'regular' ? 'Regular Bid' : 'Maximum Bid'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                By confirming, you agree to our auction terms and conditions. Your bid is legally binding.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button
            onClick={handleCloseBidModal}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Box>
            {bidStep === 2 && (
              <Button
                onClick={handleBidBack}
                size="small"
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            )}
            {bidStep === 1 ? (
              <Button
                onClick={handleBidNext}
                variant="contained"
                size="small"
                disabled={!bidForm.fullName || !bidForm.email || !bidForm.phone || !bidForm.bidAmount}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmitBid}
                variant="contained"
                size="small"
                color="primary"
              >
                Confirm Bid
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            backgroundColor: alpha('#C6A75E', 0.1),
            color: '#2D2D2D',
            border: `1px solid ${alpha('#C6A75E', 0.3)}`,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'primary.main',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Auctions;